import React from 'react';
import { Tooltip } from '@mui/material';

import styles from './avatars.module.scss';
import { sortAvatarItemsByName } from './helpers';
import { getVisibleItemsWithRemaining } from '../../../utils/visible-items';

export type AvatarsItem = {
    id: string | number;
    name?: string | null;
};

type AvatarsListProps = {
    items: AvatarsItem[];
    visibleCount?: number;
};

const MATERIAL_COLORS = [
    '#F44336',
    '#E91E63',
    '#9C27B0',
    '#673AB7',
    '#3F51B5',
    '#2196F3',
    '#03A9F4',
    '#00BCD4',
    '#009688',
    '#4CAF50',
    '#8BC34A',
    '#CDDC39',
    '#FFEB3B',
    '#FFC107',
    '#FF9800',
    '#FF5722',
    '#795548',
    '#9E9E9E',
    '#607D8B',
];

const getColor = (text?: string | null): string => {
    const source = text ?? '';
    if (!source) {
        return MATERIAL_COLORS[0];
    }

    let hash = 0;
    for (let i = 0; i < source.length; i++) {
        hash = source.charCodeAt(i) + ((hash << 5) - hash);
    }

    const index = Math.abs(hash) % MATERIAL_COLORS.length;
    return MATERIAL_COLORS[index];
};

const getInitials = (name?: string | null) => {
    if (!name) {
        return '';
    }

    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length === 0) {
        return '';
    }

    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
    }

    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

export const AvatarsList: React.FC<AvatarsListProps> = ({
    items,
    visibleCount = 7,
}) => {
    if (!items.length) {
        return null;
    }

    const sortedItems = sortAvatarItemsByName(items);
    const { visibleItems, remainingCount } = getVisibleItemsWithRemaining(sortedItems, visibleCount);

    return (
        <div className={styles.avatars}>
            {visibleItems.map(item => (
                <Tooltip key={item.id} title={item.name ?? ''}>
                    <span
                        className={styles.avatar}
                        style={{ backgroundColor: getColor(item.name) }}>
                        {getInitials(item.name)}
                    </span>
                </Tooltip>
            ))}
            {remainingCount > 0 ? (
                <span className={`${styles.avatar} ${styles.avatarMore}`}>
                    +{remainingCount}
                </span>
            ) : null}
        </div>
    );
};

export default AvatarsList;
